'use client';

import { c as _c } from "react/compiler-runtime";
import { isHostedFundingMethod } from '@morfi/onramp-sdk';
import type { useFundingAmountDraft, useFundingInlineCheckout } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { BankTransferInstructions } from '../bank-transfers/bank-transfer-instructions';
import { FundingReceipt } from '../history/funding-receipt';
import { FundingProfile } from '../profile/funding-profile';
import { FundingLoading } from '../shared/funding-loading';
import { FundingCustomerVerificationForm } from '../verification/funding-customer-verification';
import { FundingVerification } from '../verification/funding-verification';
import { FundingAuthenticationTerms } from './funding-authentication-terms';
import { FundingCardEntry } from './funding-card-entry';
import type { Props } from './funding-checkout';
import { FundingCheckoutAmount } from './funding-checkout-amount';
import { FundingCheckoutFeedback } from './funding-checkout-feedback';
import { FundingConnectionRequired } from './funding-connection-required';
import { FundingMethodPicker } from './funding-method-picker';
import { FundingPaymentReview } from './funding-payment-review';
import { FundingProviderFrame } from './funding-provider-frame';
import { FundingHostedReview } from './hosted/funding-hosted-review';
import { FundingHostedWidget } from './hosted/funding-hosted-widget';
export function FundingCheckoutView(t0) {
  const $ = _c(142);
  const {
    checkout,
    amountDraft,
    client,
    realtime,
    sessionProofId,
    authenticationBusy,
    authenticationError,
    onAcceptAuthenticationTerms,
    provider,
    onSessionRefresh,
    onSignIn,
    onExchange,
    currency,
    onClose,
    directPurchase,
    decimals
  } = t0;
  const t = useTranslations("onramp");
  if (checkout.loading && checkout.state === "methods") {
    const t1 = Boolean(directPurchase);
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [];
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== client || $[2] !== onExchange || $[3] !== t1) {
      t3 = <FundingMethodPicker directPurchase={t1} client={client} methods={t2} onExchange={onExchange} loading={true} />;
      $[1] = client;
      $[2] = onExchange;
      $[3] = t1;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (checkout.loading && !checkout.paymentSignature) {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FundingLoading />;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (checkout.state === "profile") {
    const t1 = checkout.review?.estimate.currency ?? checkout.preview.review?.estimate.currency ?? currency;
    const t2 = checkout.review?.estimate.decimals ?? checkout.preview.review?.estimate.decimals ?? decimals;
    const t3 = Boolean(checkout.error) && checkout.error !== "disconnect_failed";
    let t4;
    if ($[6] !== checkout.back || $[7] !== checkout.busy || $[8] !== checkout.canDisconnect || $[9] !== checkout.disconnect || $[10] !== checkout.profileConnection || $[11] !== checkout.profileUpdated || $[12] !== checkout.profileVerify || $[13] !== client || $[14] !== provider || $[15] !== sessionProofId || $[16] !== t1 || $[17] !== t2 || $[18] !== t3) {
      t4 = <FundingProfile canDisconnect={checkout.canDisconnect} disconnectBusy={checkout.busy} onDisconnect={checkout.disconnect} connection={checkout.profileConnection} sessionProofId={sessionProofId} client={client} provider={provider} currency={t1} decimals={t2} onBack={checkout.back} onUpdated={checkout.profileUpdated} onVerify={checkout.profileVerify} verificationError={t3} />;
      $[6] = checkout.back;
      $[7] = checkout.busy;
      $[8] = checkout.canDisconnect;
      $[9] = checkout.disconnect;
      $[10] = checkout.profileConnection;
      $[11] = checkout.profileUpdated;
      $[12] = checkout.profileVerify;
      $[13] = client;
      $[14] = provider;
      $[15] = sessionProofId;
      $[16] = t1;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  if (checkout.state === "terms") {
    let t1;
    if ($[20] !== authenticationBusy || $[21] !== authenticationError || $[22] !== client || $[23] !== onAcceptAuthenticationTerms) {
      t1 = <FundingAuthenticationTerms client={client} busy={authenticationBusy} error={authenticationError} onAccept={onAcceptAuthenticationTerms} />;
      $[20] = authenticationBusy;
      $[21] = authenticationError;
      $[22] = client;
      $[23] = onAcceptAuthenticationTerms;
      $[24] = t1;
    } else {
      t1 = $[24];
    }
    const terms = t1;
    let t2;
    if ($[25] !== client || $[26] !== directPurchase || $[27] !== onExchange || $[28] !== terms) {
      t2 = directPurchase ? terms : <FundingMethodPicker client={client} methods={[]} onExchange={onExchange} providerFeedback={terms} />;
      $[25] = client;
      $[26] = directPurchase;
      $[27] = onExchange;
      $[28] = terms;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    return t2;
  }
  if (checkout.state === "verification") {
    const t1 = checkout.location?.country ?? undefined;
    let t2;
    if ($[30] !== checkout.back || $[31] !== client || $[32] !== onSessionRefresh || $[33] !== t1) {
      t2 = <FundingVerification client={client} onContinue={onSessionRefresh} onBack={checkout.back} country={t1} />;
      $[30] = checkout.back;
      $[31] = client;
      $[32] = onSessionRefresh;
      $[33] = t1;
      $[34] = t2;
    } else {
      t2 = $[34];
    }
    return t2;
  }
  if (checkout.state === "customer_verification" && checkout.customerVerification) {
    let t1;
    if ($[35] !== checkout.customerVerification || $[36] !== checkout.retry || $[37] !== checkout.verificationFromProfile || $[38] !== client || $[39] !== provider) {
      t1 = <FundingCustomerVerificationForm client={client} key={checkout.customerVerification.customerId} provider={provider} verification={checkout.customerVerification} onComplete={checkout.retry} fromProfile={checkout.verificationFromProfile} />;
      $[35] = checkout.customerVerification;
      $[36] = checkout.retry;
      $[37] = checkout.verificationFromProfile;
      $[38] = client;
      $[39] = provider;
      $[40] = t1;
    } else {
      t1 = $[40];
    }
    const verification = t1;
    let t2;
    if ($[41] !== checkout.verificationFromProfile || $[42] !== client || $[43] !== directPurchase || $[44] !== onExchange || $[45] !== verification) {
      t2 = directPurchase || checkout.verificationFromProfile ? verification : <FundingMethodPicker client={client} methods={[]} onExchange={onExchange} providerFeedback={verification} providerFeedbackOwnsStage={true} />;
      $[41] = checkout.verificationFromProfile;
      $[42] = client;
      $[43] = directPurchase;
      $[44] = onExchange;
      $[45] = verification;
      $[46] = t2;
    } else {
      t2 = $[46];
    }
    return t2;
  }
  if (checkout.state === "status" && checkout.pendingId) {
    let t1;
    if ($[47] !== checkout) {
      t1 = checkout.error === "unsupported" ? <FundingCheckoutFeedback checkout={checkout} /> : null;
      $[47] = checkout;
      $[48] = t1;
    } else {
      t1 = $[48];
    }
    let t2;
    if ($[49] !== checkout.operation?.id || $[50] !== checkout.pendingId || $[51] !== checkout.review) {
      t2 = checkout.review && checkout.operation?.id === checkout.pendingId ? {
        quoted: {
          operationId: checkout.pendingId,
          review: checkout.review
        }
      } : {};
      $[49] = checkout.operation?.id;
      $[50] = checkout.pendingId;
      $[51] = checkout.review;
      $[52] = t2;
    } else {
      t2 = $[52];
    }
    let t3;
    if ($[53] !== realtime) {
      t3 = realtime ? {
        realtime
      } : {};
      $[53] = realtime;
      $[54] = t3;
    } else {
      t3 = $[54];
    }
    let t4;
    if ($[55] !== checkout.back || $[56] !== checkout.pendingId || $[57] !== client || $[58] !== onClose || $[59] !== onSignIn || $[60] !== t2 || $[61] !== t3) {
      t4 = <FundingReceipt onReturnToPurchase={onClose} progress={true} {...t2} client={client} {...t3} operationId={checkout.pendingId} onBack={checkout.back} onSignIn={onSignIn} />;
      $[55] = checkout.back;
      $[56] = checkout.pendingId;
      $[57] = client;
      $[58] = onClose;
      $[59] = onSignIn;
      $[60] = t2;
      $[61] = t3;
      $[62] = t4;
    } else {
      t4 = $[62];
    }
    let t5;
    if ($[63] !== checkout || $[64] !== t) {
      t5 = checkout.canResumeChallenge ? <AnimatedButton variant="secondary" loading={checkout.busy} disabled={checkout.busy} onClick={() => {
        checkout.resumeChallenge();
      }}>{t("resume_verification")}</AnimatedButton> : null;
      $[63] = checkout;
      $[64] = t;
      $[65] = t5;
    } else {
      t5 = $[65];
    }
    let t6;
    if ($[66] !== t1 || $[67] !== t4 || $[68] !== t5) {
      t6 = <>{t1}{t4}{t5}</>;
      $[66] = t1;
      $[67] = t4;
      $[68] = t5;
      $[69] = t6;
    } else {
      t6 = $[69];
    }
    return t6;
  }
  if (checkout.state === "connection_required" || checkout.state === "unavailable" || checkout.state === "methods" && !checkout.error && (checkout.methods.length === 0 || checkout.authenticationAvailable)) {
    const t1 = Boolean(directPurchase);
    const t2 = checkout.state !== "unavailable" && checkout.authenticationReason === "region" && checkout.authenticationAvailable;
    const t3 = checkout.state !== "unavailable" && checkout.authenticationAvailable;
    let t4;
    if ($[70] !== authenticationBusy || $[71] !== authenticationError || $[72] !== checkout.authenticationReason || $[73] !== checkout.connect || $[74] !== checkout.location || $[75] !== checkout.retry || $[76] !== t3) {
      t4 = <FundingConnectionRequired reason={checkout.authenticationReason} location={checkout.location} onRefresh={checkout.retry} onContinue={checkout.connect} canAuthenticate={t3} busy={authenticationBusy} error={authenticationError} />;
      $[70] = authenticationBusy;
      $[71] = authenticationError;
      $[72] = checkout.authenticationReason;
      $[73] = checkout.connect;
      $[74] = checkout.location;
      $[75] = checkout.retry;
      $[76] = t3;
      $[77] = t4;
    } else {
      t4 = $[77];
    }
    let t5;
    if ($[78] !== checkout.methods || $[79] !== checkout.openProfile || $[80] !== checkout.select || $[81] !== client || $[82] !== onExchange || $[83] !== t1 || $[84] !== t2 || $[85] !== t4) {
      t5 = <FundingMethodPicker directPurchase={t1} client={client} methods={checkout.methods} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} feedbackAfterMethods={t2} providerFeedback={t4} />;
      $[78] = checkout.methods;
      $[79] = checkout.openProfile;
      $[80] = checkout.select;
      $[81] = client;
      $[82] = onExchange;
      $[83] = t1;
      $[84] = t2;
      $[85] = t4;
      $[86] = t5;
    } else {
      t5 = $[86];
    }
    return t5;
  }
  if (checkout.state === "connect" && !checkout.paymentSignature) {
    let t1;
    if ($[87] !== checkout || $[88] !== provider) {
      t1 = <FundingProviderFrame key={checkout.state} provider={provider} checkout={checkout} />;
      $[87] = checkout;
      $[88] = provider;
      $[89] = t1;
    } else {
      t1 = $[89];
    }
    return t1;
  }
  if (checkout.state === "bank_transfer_instructions" && checkout.pendingId) {
    let t1;
    if ($[90] !== checkout.pendingId || $[91] !== checkout.viewPaymentStatus || $[92] !== client || $[93] !== onClose) {
      t1 = <BankTransferInstructions client={client} operationId={checkout.pendingId} onDismiss={onClose} onViewStatus={checkout.viewPaymentStatus} />;
      $[90] = checkout.pendingId;
      $[91] = checkout.viewPaymentStatus;
      $[92] = client;
      $[93] = onClose;
      $[94] = t1;
    } else {
      t1 = $[94];
    }
    return t1;
  }
  if (checkout.method && isHostedFundingMethod(checkout.method) && (checkout.state === "review" || checkout.state === "payment")) {
    let t1;
    if ($[95] !== checkout) {
      t1 = checkout.state === "review" ? <FundingHostedReview checkout={checkout} /> : null;
      $[95] = checkout;
      $[96] = t1;
    } else {
      t1 = $[96];
    }
    const t2 = checkout.state !== "payment";
    const t3 = checkout.state !== "payment";
    let t4;
    if ($[97] !== checkout || $[98] !== provider) {
      t4 = checkout.paymentSignature ? <FundingHostedWidget key={checkout.attemptId} provider={provider} checkout={checkout} /> : null;
      $[97] = checkout;
      $[98] = provider;
      $[99] = t4;
    } else {
      t4 = $[99];
    }
    let t5;
    if ($[100] !== t2 || $[101] !== t3 || $[102] !== t4) {
      t5 = <div hidden={t2} inert={t3}>{t4}</div>;
      $[100] = t2;
      $[101] = t3;
      $[102] = t4;
      $[103] = t5;
    } else {
      t5 = $[103];
    }
    let t6;
    if ($[104] !== t1 || $[105] !== t5) {
      t6 = <div className="flex flex-col gap-4">{t1}{t5}</div>;
      $[104] = t1;
      $[105] = t5;
      $[106] = t6;
    } else {
      t6 = $[106];
    }
    return t6;
  }
  if (checkout.state === "card_entry") {
    let t1;
    if ($[107] !== checkout) {
      t1 = <FundingCheckoutFeedback checkout={checkout} />;
      $[107] = checkout;
      $[108] = t1;
    } else {
      t1 = $[108];
    }
    let t2;
    if ($[109] !== checkout || $[110] !== provider) {
      t2 = <FundingCardEntry provider={provider} checkout={checkout} />;
      $[109] = checkout;
      $[110] = provider;
      $[111] = t2;
    } else {
      t2 = $[111];
    }
    let t3;
    if ($[112] !== t1 || $[113] !== t2) {
      t3 = <>{t1}{t2}</>;
      $[112] = t1;
      $[113] = t2;
      $[114] = t3;
    } else {
      t3 = $[114];
    }
    return t3;
  }
  let t1;
  if ($[115] !== checkout || $[116] !== provider) {
    t1 = <FundingPaymentReview provider={provider} checkout={checkout} />;
    $[115] = checkout;
    $[116] = provider;
    $[117] = t1;
  } else {
    t1 = $[117];
  }
  let t2;
  if ($[118] !== checkout) {
    t2 = !checkout.amountEntry && !(checkout.method === "sepa" && checkout.state === "review") ? <FundingCheckoutFeedback checkout={checkout} /> : null;
    $[118] = checkout;
    $[119] = t2;
  } else {
    t2 = $[119];
  }
  let t3;
  if ($[120] !== amountDraft || $[121] !== checkout || $[122] !== provider) {
    t3 = <FundingCheckoutAmount checkout={checkout} amountDraft={amountDraft} provider={provider} />;
    $[120] = amountDraft;
    $[121] = checkout;
    $[122] = provider;
    $[123] = t3;
  } else {
    t3 = $[123];
  }
  let t4;
  if ($[124] !== checkout || $[125] !== provider) {
    t4 = checkout.state === "connect" ? <FundingProviderFrame key="connect" provider={provider} checkout={checkout} /> : null;
    $[124] = checkout;
    $[125] = provider;
    $[126] = t4;
  } else {
    t4 = $[126];
  }
  let t5;
  if ($[127] !== checkout || $[128] !== provider) {
    t5 = checkout.state === "challenge" ? <FundingProviderFrame key="challenge" provider={provider} checkout={checkout} /> : null;
    $[127] = checkout;
    $[128] = provider;
    $[129] = t5;
  } else {
    t5 = $[129];
  }
  let t6;
  if ($[130] !== checkout || $[131] !== client || $[132] !== directPurchase || $[133] !== onExchange) {
    t6 = checkout.state === "methods" ? <FundingMethodPicker directPurchase={Boolean(directPurchase)} client={client} methods={checkout.methods} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} savedCards={checkout.savedCards} busy={checkout.busy} onSelectCard={checkout.selectSavedCard} onRemoveCard={id => {
      checkout.deleteSavedCard(id);
    }} /> : null;
    $[130] = checkout;
    $[131] = client;
    $[132] = directPurchase;
    $[133] = onExchange;
    $[134] = t6;
  } else {
    t6 = $[134];
  }
  let t7;
  if ($[135] !== t1 || $[136] !== t2 || $[137] !== t3 || $[138] !== t4 || $[139] !== t5 || $[140] !== t6) {
    t7 = <div className="flex flex-col gap-4">{t1}{t2}{t3}{t4}{t5}{t6}</div>;
    $[135] = t1;
    $[136] = t2;
    $[137] = t3;
    $[138] = t4;
    $[139] = t5;
    $[140] = t6;
    $[141] = t7;
  } else {
    t7 = $[141];
  }
  return t7;
}